import { useState, useEffect, useMemo } from 'react';
import { Report, Zone } from './types';
import { useAuth } from './hooks/useAuth';
import { RepositoryService } from './services/RepositoryService';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { ErrorProvider } from './contexts/ErrorContext';
import PageTransition, {
  FadeIn,
  SlideUp,
} from './components/ui/page-transition';
import MapView from './components/MapView';
import ReportsList from './components/ReportsList';
import ReportButton from './components/ReportButton';
import ReportModal from './components/ReportModal';
import GoogleMapsProvider from './components/GoogleMapsProvider';

import ReportDetail from './components/ReportDetail';
import AuthForm from './components/AuthForm';
import ZonesList from './components/ZonesList';
import ZoneDetail from './components/ZoneDetail';
import ZoneCreator from './components/ZoneCreator';
import { Button } from './components/ui/button';
import { Dialog, DialogContent, DialogTitle } from './components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './components/ui/tabs';
import {
  List,
  Map,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Download,
} from 'lucide-react';
import { useInstallPWA } from './hooks/useInstallPWA';
import SplashScreen from './components/SplashScreen';
import { useIsMobile } from './hooks/useIsMobile';
import { useGeolocation } from './hooks/useGeolocation';
import { useIPGeolocation } from './hooks/useIPGeolocation';

// Fonction pour vérifier si un point est dans un polygone
function isPointInPolygon(
  point: { lat: number; lng: number },
  polygon: Array<{ lat: number; lng: number }>
) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lat,
      yi = polygon[i].lng;
    const xj = polygon[j].lat,
      yj = polygon[j].lng;

    const intersect =
      yi > point.lng !== yj > point.lng &&
      point.lat < ((xj - xi) * (point.lng - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function AppContent() {
  const {
    user,
    isAuthenticated,
    isAdmin,
    isZoneManager,
    isLoading,
    handleLogin,
    handleRegister,
    handleLogout,
    resetPassword,
  } = useAuth();
  const toast = useToast();
  const { withLoading } = useLoading();
  const isMobile = useIsMobile();
  const { install } = useInstallPWA();

  // États pour les signalements
  const [selectedReportId, setSelectedReportId] = useState<string>();
  const [reports, setReports] = useState<Report[]>([]);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isReportDetailOpen, setIsReportDetailOpen] = useState(false);
  const [detailReportId, setDetailReportId] = useState<string>();
  const [clickedLocation, setClickedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // États pour les zones
  const [zones, setZones] = useState<Zone[]>([]);
  const [userZones, setUserZones] = useState<Zone[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<string>();
  const [hoveredZoneId, setHoveredZoneId] = useState<string>();
  const [isZoneDetailOpen, setIsZoneDetailOpen] = useState(false);
  const [detailZoneId, setDetailZoneId] = useState<string>();
  const [isZoneCreatorOpen, setIsZoneCreatorOpen] = useState(false);

  // État de navigation
  const [adminActiveTab, setAdminActiveTab] = useState<'reports' | 'zones'>(
    'reports'
  );
  const [mobileView, setMobileView] = useState<'map' | 'list'>('map');
  const [isRightPanelExpanded, setIsRightPanelExpanded] = useState(false);

  // État pour centrer la carte
  const [centerOnLocation, setCenterOnLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [zoomToBounds, setZoomToBounds] = useState<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);
  const [currentMapCenter, setCurrentMapCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [currentMapZoom, setCurrentMapZoom] = useState<number>(13);

  // Position de l'utilisateur avec géolocalisation réelle
  const { position: geoPosition } = useGeolocation();
  const { ipLocation } = useIPGeolocation();

  // Fonction pour sauvegarder la position actuelle
  const saveUserPosition = (lat: number, lng: number) => {
    localStorage.setItem(
      'lastUserPosition',
      JSON.stringify({ latitude: lat, longitude: lng })
    );
  };

  // Fonction pour récupérer la dernière position sauvegardée
  const getLastSavedPosition = () => {
    const saved = localStorage.getItem('lastUserPosition');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  // Position par défaut (Tour Eiffel)
  const defaultPosition = {
    latitude: 48.8584,
    longitude: 2.2945,
    address: 'Tour Eiffel, Paris',
  };

  // Logique de priorité pour la position utilisateur
  const userLocation = useMemo(() => {
    // 1. Géolocalisation en temps réel
    if (geoPosition) {
      return geoPosition;
    }

    // 2. Dernière position sauvegardée
    const lastPosition = getLastSavedPosition();
    if (lastPosition) {
      return {
        ...lastPosition,
        address: 'Dernière position connue',
      };
    }

    // 3. Géolocalisation par IP
    if (ipLocation) {
      return {
        latitude: ipLocation.latitude,
        longitude: ipLocation.longitude,
        address:
          ipLocation.city && ipLocation.country
            ? `${ipLocation.city}, ${ipLocation.country}`
            : 'Position approximative',
      };
    }

    // 4. Position par défaut
    return defaultPosition;
  }, [geoPosition, ipLocation]);

  // Mémoriser la location pour ReportModal pour éviter les re-rendus
  const reportModalUserLocation = useMemo(
    () =>
      clickedLocation
        ? {
            latitude: clickedLocation.lat,
            longitude: clickedLocation.lng,
            address: 'Position sélectionnée sur la carte',
          }
        : userLocation,
    [clickedLocation, userLocation]
  );

  // Charger les données au démarrage
  useEffect(() => {
    if (isAuthenticated) {
      loadReports();
      loadZones();
    }
  }, [isAuthenticated]);

  // Gérer l'ouverture automatique d'un signalement depuis l'URL
  useEffect(() => {
    // Vérifier s'il y a un paramètre sig au démarrage
    const urlParams = new URLSearchParams(window.location.search);
    const sigParam = urlParams.get('sig');

    if (sigParam && isAuthenticated && reports.length > 0) {
      const reportId = sigParam;
      const report = reports.find((r) => r.id === reportId);

      // Nettoyer immédiatement l'URL
      window.history.pushState({}, '', window.location.pathname);

      if (report) {
        // Ouvrir la modale de détail
        setDetailReportId(reportId);
        setIsReportDetailOpen(true);

        // Sélectionner le signalement sur la carte
        setSelectedReportId(reportId);
      } else {
        toast.error(
          'Signalement introuvable',
          "Ce signalement n'existe pas ou n'est plus accessible"
        );
      }
    }
  }, [isAuthenticated, reports, toast]);

  const loadReports = async () => {
    try {
      const reportsList = await RepositoryService.reports.getAll();
      setReports(reportsList);
    } catch (error) {
      console.error('Erreur lors du chargement des signalements:', error);
      toast.error('Erreur', 'Impossible de charger les signalements');
    }
  };

  const loadZones = async () => {
    try {
      const zonesList = await RepositoryService.zones.getAll();
      setZones(zonesList);

      // Filtrer les zones pour les gestionnaires
      if (isZoneManager && user?.zones) {
        const zones = zonesList.filter((zone) => user.zones?.includes(zone.id));
        setUserZones(zones);
      } else if (isAdmin) {
        setUserZones(zonesList);
      } else {
        setUserZones([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des zones:', error);
      toast.error('Erreur', 'Impossible de charger les zones');
    }
  };

  // Fonctions pour les signalements
  const handleReportClick = (report: Report) => {
    setSelectedReportId(report.id === selectedReportId ? undefined : report.id);
    // Centrer la carte sur le signalement
    setCenterOnLocation({ lat: report.latitude, lng: report.longitude });
    setZoomToBounds(null); // Réinitialiser les bounds
    // Sur mobile, basculer en vue carte pour voir le signalement
    if (isMobile && mobileView === 'list') {
      setMobileView('map');
    }
  };

  const handleNewReport = () => {
    setIsReportModalOpen(true);
  };

  const handleNewReportFromButton = () => {
    setClickedLocation(null); // Reset pour utiliser la géoloc user
    setIsReportModalOpen(true);
  };

  const handleOpenReportDetail = (reportOrId: Report | string) => {
    const reportId =
      typeof reportOrId === 'string' ? reportOrId : reportOrId.id;
    setDetailReportId(reportId);
    setIsReportDetailOpen(true);
  };

  const handleCloseReportDetail = () => {
    setIsReportDetailOpen(false);
    setDetailReportId(undefined);
  };

  // Fonction pour générer un lien public vers un signalement
  const generateReportLink = (reportId: string) => {
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?sig=${reportId}`;
  };

  const handleMapClick = (
    event: React.MouseEvent<HTMLDivElement>,
    lat?: number,
    lng?: number
  ) => {
    const target = event.target as HTMLElement;
    if (target && target.closest && target.closest('button')) {
      return;
    }
    if (lat !== undefined && lng !== undefined) {
      // Vérifier si on a cliqué sur notre position (avec une petite tolérance)
      const isUserLocation =
        userLocation &&
        Math.abs(lat - userLocation.latitude) < 0.0001 &&
        Math.abs(lng - userLocation.longitude) < 0.0001;

      if (isUserLocation) {
        // Si c'est notre position, ne pas définir clickedLocation pour utiliser userLocation directement
        setClickedLocation(null);
      } else {
        setClickedLocation({ lat, lng });
      }
    } else {
      setClickedLocation(null);
    }
    handleNewReport();
  };

  const handleSubmitReport = async (formData: any) => {
    try {
      const newReport: Omit<Report, 'id'> = {
        title:
          formData.description.length > 50
            ? formData.description.substring(0, 50) + '...'
            : formData.description,
        date: new Date().toISOString(),
        location: formData.location.address,
        latitude: formData.location.latitude,
        longitude: formData.location.longitude,
        status: 'in_progress',
        description: formData.description,
      };

      // Sauvegarder la position lors de la création d'un signalement
      saveUserPosition(formData.location.latitude, formData.location.longitude);

      const createdReport = await withLoading(
        () => RepositoryService.reports.create(newReport, formData.photo),
        'Création du signalement...'
      );

      setReports((prevReports) => [createdReport, ...prevReports]);
      setSelectedReportId(createdReport.id);

      toast.success(
        'Signalement créé',
        'Votre signalement a été enregistré avec succès'
      );
    } catch (error) {
      console.error('Erreur lors de la création du signalement:', error);
      toast.error('Erreur', 'Impossible de créer le signalement');
    }
  };

  // Fonctions pour les zones
  const handleZoneClick = (zone: Zone) => {
    setSelectedZoneId(zone.id === selectedZoneId ? undefined : zone.id);

    // Centrer la carte sur la zone
    if (zone.coordinates.length > 0) {
      // Calculer les limites de la zone
      let minLat = zone.coordinates[0].latitude;
      let maxLat = zone.coordinates[0].latitude;
      let minLng = zone.coordinates[0].longitude;
      let maxLng = zone.coordinates[0].longitude;

      zone.coordinates.forEach((coord) => {
        minLat = Math.min(minLat, coord.latitude);
        maxLat = Math.max(maxLat, coord.latitude);
        minLng = Math.min(minLng, coord.longitude);
        maxLng = Math.max(maxLng, coord.longitude);
      });

      // Centrer sur le centre de la zone et définir les limites
      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;

      setCenterOnLocation({ lat: centerLat, lng: centerLng });
      setZoomToBounds({
        north: maxLat,
        south: minLat,
        east: maxLng,
        west: minLng,
      });

      // Sur mobile, basculer en vue carte pour voir la zone
      if (isMobile && mobileView === 'list') {
        setMobileView('map');
      }
    }
  };

  const handleZoneHover = (zoneId: string | undefined) => {
    setHoveredZoneId(zoneId);
  };

  const handleOpenZoneDetail = (zoneOrId: Zone | string) => {
    const zoneId = typeof zoneOrId === 'string' ? zoneOrId : zoneOrId.id;
    const zone = zones.find((z) => z.id === zoneId);
    if (zone) {
      // Sélectionner la zone
      setSelectedZoneId(zoneId);
      // Centrer la carte sur la zone (même logique que handleZoneClick)
      if (zone.coordinates.length > 0) {
        let minLat = zone.coordinates[0].latitude;
        let maxLat = zone.coordinates[0].latitude;
        let minLng = zone.coordinates[0].longitude;
        let maxLng = zone.coordinates[0].longitude;

        zone.coordinates.forEach((coord) => {
          minLat = Math.min(minLat, coord.latitude);
          maxLat = Math.max(maxLat, coord.latitude);
          minLng = Math.min(minLng, coord.longitude);
          maxLng = Math.max(maxLng, coord.longitude);
        });

        const centerLat = (minLat + maxLat) / 2;
        const centerLng = (minLng + maxLng) / 2;

        setCenterOnLocation({ lat: centerLat, lng: centerLng });
        setZoomToBounds({
          north: maxLat,
          south: minLat,
          east: maxLng,
          west: minLng,
        });
      }
    }
    // Ouvrir les détails
    setDetailZoneId(zoneId);
    setIsZoneDetailOpen(true);
  };

  const handleCloseZoneDetail = () => {
    setIsZoneDetailOpen(false);
    setDetailZoneId(undefined);
  };

  const handleUpdateZone = async (zoneId: string, zoneData: Partial<Zone>) => {
    try {
      const updatedZone = await withLoading(
        () => RepositoryService.zones.update(zoneId, zoneData),
        'Mise à jour de la zone...'
      );
      setZones(zones.map((zone) => (zone.id === zoneId ? updatedZone : zone)));
      setUserZones(
        userZones.map((zone) => (zone.id === zoneId ? updatedZone : zone))
      );
      // Zone updated successfully
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la zone:', error);
      throw error;
    }
  };

  // Fonction pour mettre à jour le statut d'un signalement
  const handleUpdateReportStatus = async (
    reportId: string,
    status: 'in_progress' | 'done'
  ) => {
    try {
      const updatedReport = await withLoading(
        () => RepositoryService.reports.update(reportId, { status }),
        `${status === 'done' ? 'Fermeture' : 'Réouverture'} du signalement...`
      );
      setReports(
        reports.map((report) =>
          report.id === reportId ? updatedReport : report
        )
      );
      toast.success(
        status === 'done' ? 'Signalement terminé' : 'Signalement rouvert',
        `Le statut a été mis à jour`
      );
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      toast.error('Erreur', 'Impossible de mettre à jour le statut');
    }
  };

  // Fonction pour supprimer un signalement
  const handleDeleteReport = async (reportId: string) => {
    try {
      await withLoading(
        () => RepositoryService.reports.delete(reportId),
        'Suppression du signalement...'
      );
      setReports(reports.filter((report) => report.id !== reportId));
      // Fermer la modale de détail si c'est le signalement affiché
      if (detailReportId === reportId) {
        setIsReportDetailOpen(false);
        setDetailReportId(undefined);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du signalement:', error);
      throw error;
    }
  };

  // Fonction pour supprimer une zone
  const handleDeleteZone = async (zoneId: string) => {
    try {
      await withLoading(
        () => RepositoryService.zones.delete(zoneId),
        'Suppression de la zone...'
      );
      setZones(zones.filter((zone) => zone.id !== zoneId));
      setUserZones(userZones.filter((zone) => zone.id !== zoneId));
      // Fermer la modale de détail si c'est la zone affichée
      if (detailZoneId === zoneId) {
        setIsZoneDetailOpen(false);
        setDetailZoneId(undefined);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de la zone:', error);
      throw error;
    }
  };

  // Fonction pour ouvrir le créateur de zone
  const handleAddZone = () => {
    setIsZoneCreatorOpen(true);
  };

  // Fonction pour sauvegarder une nouvelle zone
  const handleSaveNewZone = async (zoneData: Omit<Zone, 'id'>) => {
    try {
      // Sauvegarder la position lors de la création d'une zone (centre de la zone)
      if (zoneData.coordinates.length > 0) {
        const centerLat =
          zoneData.coordinates.reduce((sum, coord) => sum + coord.latitude, 0) /
          zoneData.coordinates.length;
        const centerLng =
          zoneData.coordinates.reduce(
            (sum, coord) => sum + coord.longitude,
            0
          ) / zoneData.coordinates.length;
        saveUserPosition(centerLat, centerLng);
      }

      const createdZone = await withLoading(
        () => RepositoryService.zones.create(zoneData),
        'Création de la zone...'
      );

      setZones((prevZones) => [...prevZones, createdZone]);

      // Mettre à jour userZones aussi
      if (isAdmin) {
        setUserZones((prevUserZones) => [...prevUserZones, createdZone]);
      } else if (isZoneManager && user?.zones?.includes(createdZone.id)) {
        setUserZones((prevUserZones) => [...prevUserZones, createdZone]);
      }

      // Sélectionner la nouvelle zone
      setSelectedZoneId(createdZone.id);

      toast.success('Zone créée', 'La zone a été enregistrée avec succès');
    } catch (error) {
      console.error('Erreur lors de la création de la zone:', error);
      throw new Error('Impossible de créer la zone');
    }
  };

  // Objets calculés
  const detailReport = detailReportId
    ? reports.find((r) => r.id === detailReportId)
    : null;
  const detailZone = detailZoneId
    ? zones.find((z) => z.id === detailZoneId)
    : null;

  // Filtrer les signalements pour les gestionnaires de zones
  const filteredReports = useMemo(() => {
    if (!isZoneManager || !userZones.length) {
      return reports;
    }

    // Pour les gestionnaires, ne montrer que les signalements dans leurs zones
    return reports.filter((report) => {
      return userZones.some((zone) => {
        // Vérifier si le signalement est dans les limites de la zone
        const isInZone = isPointInPolygon(
          { lat: report.latitude, lng: report.longitude },
          zone.coordinates.map((coord) => ({
            lat: coord.latitude,
            lng: coord.longitude,
          }))
        );
        return isInZone;
      });
    });
  }, [reports, userZones, isZoneManager]);

  // Affichage conditionnel
  if (isLoading) {
    // Écran de chargement pendant la vérification de l'authentification
    return <SplashScreen />;
  }

  if (!isAuthenticated) {
    return (
      <PageTransition>
        <AuthForm
          onLogin={handleLogin}
          onRegister={handleRegister}
          onResetPassword={resetPassword}
        />
      </PageTransition>
    );
  }

  return (
    <PageTransition className="min-h-screen h-screen w-full flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <FadeIn className="bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-100/50 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <img
            src="/logo.png"
            alt="Alerte Faune"
            className="h-10 sm:h-12 w-auto object-contain rounded-lg"
          />
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
            <span>Bonjour, {user?.displayName || user?.email}</span>
            {isAdmin && (
              <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                Admin
              </span>
            )}
            {isZoneManager && (
              <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                Gestionnaire
              </span>
            )}
          </div>

          {/* Badge admin/gestionnaire mobile */}
          {isAdmin && (
            <div className="sm:hidden bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-medium px-2 py-1 rounded-full">
              Admin
            </div>
          )}
          {isZoneManager && (
            <div className="sm:hidden bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-medium px-2 py-1 rounded-full">
              Gestionnaire
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={install}
            className="gap-1 text-xs sm:text-sm px-2 sm:px-3 border-green-300 text-green-700 hover:bg-green-50"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Installer</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="gap-1 text-xs sm:text-sm px-2 sm:px-3 border-gray-300"
          >
            <span className="hidden sm:inline">Déconnexion</span>
            <LogOut className="sm:hidden w-4 h-4" />
          </Button>
        </div>
      </FadeIn>

      <div className="flex-1 flex overflow-hidden relative">
        {/* Desktop Layout */}
        <div className="hidden lg:flex flex-1">
          {/* Carte principale */}
          {!isRightPanelExpanded && (
            <SlideUp delay={0.1} className="flex-1 p-2 sm:p-4">
              <MapView
                reports={filteredReports}
                onReportClick={handleReportClick}
                selectedReportId={selectedReportId}
                onMapClick={handleMapClick}
                onReportDetailClick={handleOpenReportDetail}
                zones={isAdmin || isZoneManager ? userZones : []}
                onZoneClick={handleZoneClick}
                selectedZoneId={selectedZoneId}
                hoveredZoneId={hoveredZoneId}
                onZoneHover={handleZoneHover}
                onZoneDetailClick={handleOpenZoneDetail}
                showZones={isAdmin || isZoneManager}
                currentView={isAdmin ? adminActiveTab : 'reports'}
                centerOnLocation={centerOnLocation}
                zoomToBounds={zoomToBounds}
                onMapChange={(center, zoom) => {
                  setCurrentMapCenter(center);
                  setCurrentMapZoom(zoom);
                }}
                initialUserLocation={userLocation}
              />
            </SlideUp>
          )}

          {/* Panel de droite - Desktop */}
          <SlideUp
            delay={0.2}
            className={`${isRightPanelExpanded ? 'flex-1' : 'w-[352px]'} bg-white/95 backdrop-blur-sm shadow-lg border-l border-gray-100/50 transition-all duration-300`}
          >
            <div className="h-full flex flex-col">
              {/* Header avec expand button */}
              <div className="border-b border-gray-100/50 px-4 py-3 bg-gray-50/30 flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsRightPanelExpanded(!isRightPanelExpanded)}
                  className="p-2 hover:bg-gray-200/50"
                  title={
                    isRightPanelExpanded
                      ? 'Réduire le panel'
                      : 'Agrandir le panel'
                  }
                >
                  {isRightPanelExpanded ? (
                    <ChevronRight className="w-4 h-4" />
                  ) : (
                    <ChevronLeft className="w-4 h-4" />
                  )}
                </Button>

                {isAdmin || isZoneManager ? (
                  <Tabs
                    value={adminActiveTab}
                    onValueChange={(value) =>
                      setAdminActiveTab(value as 'reports' | 'zones')
                    }
                    className="flex-1"
                  >
                    <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-full max-w-xs">
                      <TabsTrigger
                        value="reports"
                        className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200 text-sm"
                      >
                        Signalements
                      </TabsTrigger>
                      <TabsTrigger
                        value="zones"
                        className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200 text-sm"
                      >
                        Zones
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                ) : (
                  <h3 className="font-medium">Signalements</h3>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden">
                {isAdmin || isZoneManager ? (
                  <Tabs
                    value={adminActiveTab}
                    onValueChange={(value) =>
                      setAdminActiveTab(value as 'reports' | 'zones')
                    }
                    className="h-full"
                  >
                    <TabsContent value="reports" className="h-full mt-0">
                      <ReportsList
                        reports={filteredReports}
                        onReportClick={handleReportClick}
                        selectedReportId={selectedReportId}
                        onViewDetail={handleOpenReportDetail}
                        onUpdateStatus={
                          isAdmin || isZoneManager
                            ? handleUpdateReportStatus
                            : undefined
                        }
                        onRefresh={loadReports}
                      />
                    </TabsContent>

                    <TabsContent value="zones" className="h-full mt-0">
                      <ZonesList
                        zones={userZones}
                        onZoneClick={handleZoneClick}
                        selectedZoneId={selectedZoneId}
                        hoveredZoneId={hoveredZoneId}
                        onZoneHover={handleZoneHover}
                        onViewDetail={handleOpenZoneDetail}
                        onAddZone={isAdmin ? handleAddZone : undefined}
                      />
                    </TabsContent>
                  </Tabs>
                ) : (
                  <ReportsList
                    reports={filteredReports}
                    onReportClick={handleReportClick}
                    selectedReportId={selectedReportId}
                    onViewDetail={handleOpenReportDetail}
                    onUpdateStatus={undefined}
                    onRefresh={loadReports}
                  />
                )}
              </div>
            </div>
          </SlideUp>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden flex-1 relative overflow-hidden">
          {/* Carte toujours visible en arrière-plan */}
          <div className="absolute inset-0">
            <MapView
              reports={filteredReports}
              onReportClick={handleReportClick}
              selectedReportId={selectedReportId}
              onMapClick={handleMapClick}
              onReportDetailClick={handleOpenReportDetail}
              zones={isAdmin || isZoneManager ? userZones : []}
              onZoneClick={handleZoneClick}
              selectedZoneId={selectedZoneId}
              hoveredZoneId={hoveredZoneId}
              onZoneHover={handleZoneHover}
              onZoneDetailClick={handleOpenZoneDetail}
              showZones={isAdmin || isZoneManager}
              currentView={isAdmin ? adminActiveTab : 'reports'}
              centerOnLocation={centerOnLocation}
              zoomToBounds={zoomToBounds}
              showLocationButton={mobileView === 'map'}
              onMapChange={(center, zoom) => {
                setCurrentMapCenter(center);
                setCurrentMapZoom(zoom);
              }}
              initialUserLocation={userLocation}
            />
          </div>

          {/* Panel superposé quand mobileView === 'list' */}
          {mobileView === 'list' && (
            <SlideUp
              delay={0.2}
              className="absolute inset-0 bg-white/95 backdrop-blur-sm shadow-lg"
            >
              {isAdmin || isZoneManager ? (
                <Tabs
                  value={adminActiveTab}
                  onValueChange={(value) =>
                    setAdminActiveTab(value as 'reports' | 'zones')
                  }
                  className="h-full flex flex-col"
                >
                  <div className="border-b border-gray-100/50 px-4 py-3 bg-gray-50/30">
                    <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-full max-w-xs mx-auto">
                      <TabsTrigger
                        value="reports"
                        className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200 text-sm"
                      >
                        Signalements
                      </TabsTrigger>
                      <TabsTrigger
                        value="zones"
                        className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200 text-sm"
                      >
                        Zones
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent
                    value="reports"
                    className="flex-1 mt-0 overflow-hidden"
                  >
                    <ReportsList
                      reports={filteredReports}
                      onReportClick={handleReportClick}
                      selectedReportId={selectedReportId}
                      onViewDetail={handleOpenReportDetail}
                      onUpdateStatus={
                        isAdmin || isZoneManager
                          ? handleUpdateReportStatus
                          : undefined
                      }
                      onRefresh={loadReports}
                    />
                  </TabsContent>

                  <TabsContent
                    value="zones"
                    className="flex-1 mt-0 overflow-hidden"
                  >
                    <ZonesList
                      zones={userZones}
                      onZoneClick={handleZoneClick}
                      selectedZoneId={selectedZoneId}
                      hoveredZoneId={hoveredZoneId}
                      onZoneHover={handleZoneHover}
                      onViewDetail={handleOpenZoneDetail}
                      onAddZone={isAdmin ? handleAddZone : undefined}
                    />
                  </TabsContent>
                </Tabs>
              ) : (
                <ReportsList
                  reports={filteredReports}
                  onReportClick={handleReportClick}
                  selectedReportId={selectedReportId}
                  onViewDetail={handleOpenReportDetail}
                  onUpdateStatus={undefined}
                  onRefresh={loadReports}
                />
              )}
            </SlideUp>
          )}
        </div>

        {/* Bouton de bascule mobile - En dehors du container mobile */}
        <div
          className="lg:hidden fixed bottom-0 left-0 right-0 pb-safe flex justify-center"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <Button
            onClick={() => setMobileView(mobileView === 'map' ? 'list' : 'map')}
            className="bg-gray-900 hover:bg-gray-800 text-white rounded-full px-4 sm:px-6 py-2 sm:py-3 shadow-xl gap-1 sm:gap-2 border-0 transition-all duration-200 font-medium text-sm sm:text-base mb-4"
          >
            {mobileView === 'map' ? (
              <>
                <List className="w-4 h-4" />
                <span>
                  {isAdmin && adminActiveTab === 'zones'
                    ? `Zones (${userZones.length})`
                    : `Signalements (${filteredReports.filter((r) => r.status === 'in_progress').length})`}
                </span>
              </>
            ) : (
              <>
                <Map className="w-4 h-4" />
                <span>Voir la carte</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Bouton flottant */}
      <ReportButton onClick={handleNewReportFromButton} />

      {/* Modale de signalement */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => {
          setIsReportModalOpen(false);
          setClickedLocation(null);
        }}
        onSubmit={handleSubmitReport}
        userLocation={reportModalUserLocation}
        clickedLocation={clickedLocation}
      />

      {/* Modale de détail du signalement */}
      <Dialog open={isReportDetailOpen} onOpenChange={setIsReportDetailOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[90vw] lg:max-w-[80vw] xl:max-w-[75vw] 2xl:max-w-[70vw] max-h-[90vh] overflow-y-auto p-0 bg-white border border-gray-200 rounded-2xl">
          <DialogTitle className="sr-only">
            {detailReport
              ? `Détail du signalement: ${detailReport.title}`
              : 'Détail du signalement'}
          </DialogTitle>
          {detailReport && (
            <ReportDetail
              report={detailReport}
              onBack={handleCloseReportDetail}
              showBackButton={false}
              onGenerateLink={generateReportLink}
              onDelete={isAdmin ? handleDeleteReport : undefined}
              onUpdateStatus={
                isAdmin || isZoneManager ? handleUpdateReportStatus : undefined
              }
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modale de détail de zone */}
      {detailZone && (
        <>
          <ZoneDetail
            zone={detailZone}
            isOpen={isZoneDetailOpen}
            onClose={handleCloseZoneDetail}
            onUpdate={handleUpdateZone}
            onDelete={isAdmin ? handleDeleteZone : undefined}
          />
        </>
      )}

      {/* Modale de création de zone */}
      <ZoneCreator
        isOpen={isZoneCreatorOpen}
        onClose={() => setIsZoneCreatorOpen(false)}
        onSave={handleSaveNewZone}
        initialCenter={
          currentMapCenter || userLocation
            ? { lat: userLocation.latitude, lng: userLocation.longitude }
            : undefined
        }
        initialZoom={currentMapZoom}
      />
    </PageTransition>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <LoadingProvider>
        <ErrorProvider>
          <GoogleMapsProvider>
            <AppContent />
          </GoogleMapsProvider>
        </ErrorProvider>
      </LoadingProvider>
    </ToastProvider>
  );
}
